import { z } from "zod";
const key = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/u);
const id = z.string().trim().min(1).max(200);
const sha256 = z.string().regex(/^[0-9a-f]{64}$/u);
/** Retained evidence for a link, never an editable copy of source business facts. */
export const workflowItemSourceProvenanceSchema = z.object({
    schemaVersion: z.literal(1),
    origin: z.enum(["warehouse_relation", "connector_entity", "file_upload", "plugin_read", "channel_dataset"]),
    observedAt: z.number().int().positive(),
    sourceRunId: z.string().min(1).optional(),
    fileId: z.string().min(1).optional(),
    evidenceHash: z.string().regex(/^[0-9a-f]{64}$/u).optional(),
}).strict();
export const workflowDatasetSourceMappingSchema = z.object({
    schemaVersion: z.literal(1),
    sourceSystem: z.string().min(1).max(200),
    columns: z.record(z.string().min(1), z.string().min(1)),
    channelDataset: z.object({ channelId: id, objectTypeId: id,
        datasetId: id.optional(), schemaDigest: sha256 }).strict().optional(),
    pluginRead: z.object({ pluginSlug: id, pluginVersion: id, toolName: id,
        reviewedDefinitionHash: sha256, schemaDigest: sha256,
        policyRevisionVector: z.record(z.string(), z.number().int().nonnegative()) }).strict().optional(),
    selectors: z.array(z.discriminatedUnion("operator", [
        z.object({ field: key, operator: z.literal("eq"), value: z.union([z.string(), z.number(), z.boolean()]) }).strict(),
        z.object({ field: key, operator: z.literal("in"), values: z.array(z.union([z.string(), z.number(), z.boolean()])).min(1).max(100) }).strict(),
    ])).max(16).optional(),
    mimeTypes: z.array(z.enum(["application/pdf", "image/jpeg", "image/png", "image/webp"])).max(4).optional(),
    intakeFileFieldKey: key.optional(),
}).strict();
export const workflowSourceFreshnessPolicySchema = z.discriminatedUnion("mode", [
    z.object({ schemaVersion: z.literal(1), mode: z.literal("legacy_unknown") }).strict(),
    z.object({ schemaVersion: z.literal(1), mode: z.literal("query_only") }).strict(),
    z.object({ schemaVersion: z.literal(1), mode: z.literal("connector"),
        integrationId: z.string().min(1), maxStalenessMs: z.number().int().positive() }).strict(),
]);
const resourceKind = z.enum(["dataset", "source", "view"]);
export const workflowResourcePlanDiffSchema = z.object({
    schemaVersion: z.literal(1),
    operations: z.array(z.object({
        action: z.enum(["create", "bind", "update"]), resourceKind, resourceKey: key,
        existingResourceId: id.optional(), expectedRevision: z.number().int().positive().optional(),
        desiredDigest: sha256,
        /** Workflow- and reviewer-owned fields of the dataset this version no longer declares: applying the plan
         * makes them ordinary fields, keeping their values, so people can edit or archive them. */
        releasedFields: z.array(key).min(1).max(64).optional(),
    }).strict()).max(160),
}).strict();
export const workflowValidationEvidenceSchema = z.object({
    schemaVersion: z.literal(1), checkedAt: z.number().int().positive(),
    principalId: id,
    inspected: z.array(z.object({ resourceKind, resourceKey: key,
        resourceId: id.optional(), revision: z.number().int().positive().optional(),
        schemaDigest: sha256.optional() }).strict()).max(160),
    unavailableSources: z.array(key).max(64),
}).strict();
export const workflowActivationReceiptSchema = z.object({
    schemaVersion: z.literal(1), appliedAt: z.number().int().positive(), planHash: sha256,
    bindings: z.array(z.object({ resourceKey: key, resourceKind, resourceId: id }).strict()).max(160),
}).strict();
export const workflowTriggerCursorSchema = z.object({
    schemaVersion: z.literal(1), watermarkMs: z.number().int().nonnegative().optional(),
    continuation: z.string().max(4000).optional(),
}).strict();
export const workflowRunInputManifestSchema = z.object({
    schemaVersion: z.literal(1), activationId: id, observedAt: z.number().int().positive(),
    itemIds: z.array(id).min(1).max(100), sourceRevisions: z.record(id, z.string().max(500)),
}).strict();
export const workflowRunCheckpointSchema = z.object({
    schemaVersion: z.literal(1),
    effectReceipts: z.record(sha256, z.object({ kind: z.enum([
            "item_upsert", "proposal_append", "extract_document",
        ]), requestHash: sha256, output: z.json() }).strict()).refine((receipts) => Object.keys(receipts).length <= 128 && JSON.stringify(receipts).length <= 262_144, "Effect receipts exceed the retained checkpoint limit"),
    sourceObservations: z.record(z.string().regex(/^ctx_[0-9a-f]{40}$/u), z.object({
        sourceKey: key, nodeId: key, effectKey: key,
        externalId: z.string().min(1).max(500),
        subrecordKey: z.string().min(1).max(500).optional(),
        sourceRevision: z.string().min(1).max(500),
        /**
         * A digest of the read's field values (not its revision or internal fields), so a review can tell a record whose
         * revision moved but whose values read the same, such as one written then put back, from one that changed.
         */
        valuesDigest: z.string().regex(/^[0-9a-f]{64}$/u).optional(),
        selectedFields: z.array(key).min(1).max(30).optional(),
        observedAt: z.number().int().positive(),
    }).strict()).refine((observations) => Object.keys(observations).length <= 256 &&
        JSON.stringify(observations).length <= 131_072, "Source observations exceed the retained checkpoint limit").optional(),
    reviewObservations: z.record(z.string().regex(/^rev_[0-9a-f]{40}$/u), z.object({
        proposalId: id, decisionId: id, itemId: id, nodeId: key, effectKey: key,
    }).strict()).refine(value => Object.keys(value).length <= 40).optional(),
    lastNodeId: key.optional(),
}).strict();
